/**
 * Rheina contact form — Shopify contact endpoint with modal feedback.
 */
import { Component } from '@theme/component';

class RheinaContact extends Component {
  /** @type {number | null} */
  #autoCloseTimer = null;

  connectedCallback() {
    super.connectedCallback();
    const form = this.querySelector('[data-contact-form]');
    form?.addEventListener('submit', this.#handleSubmit);

    const closeBtn = this.querySelector('[data-modal-close]');
    const backdrop = this.querySelector('[data-modal-backdrop]');
    closeBtn?.addEventListener('click', this.#closeModal);
    backdrop?.addEventListener('click', this.#closeModal);

    document.addEventListener('keydown', this.#onKeydown);

    if (this.dataset.showSuccess === 'true') {
      this.#openModal();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const form = this.querySelector('[data-contact-form]');
    form?.removeEventListener('submit', this.#handleSubmit);
    document.removeEventListener('keydown', this.#onKeydown);
    if (this.#autoCloseTimer !== null) clearTimeout(this.#autoCloseTimer);
    document.documentElement.classList.remove('rheina-contact--modal-open');
  }

  #onKeydown = (event) => {
    const modal = this.querySelector('[data-success-modal]');
    if (event.key === 'Escape' && modal?.dataset.open === 'true') this.#closeModal();
  };

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    event.preventDefault();

    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;

    if (!form.reportValidity()) return;

    const submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn instanceof HTMLButtonElement) {
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');
    }

    this.#clearErrors();

    const formData = new FormData(form);
    const params = new URLSearchParams();
    params.set('form_type', 'contact');
    params.set('utf8', '✓');

    for (const [key, value] of formData.entries()) {
      if (typeof value === 'string') {
        params.set(key, value);
      }
    }

    try {
      const response = await fetch('/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
        },
        body: params.toString(),
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        if (response.ok) {
          form.reset();
          this.#openModal();
          return;
        }
        throw new Error('Invalid response');
      }

      if (data?.errors) {
        this.#showFormErrors(data.errors, form);
        return;
      }

      if (!response.ok && !data?.posted_successfully) {
        this.#showInlineError(this.dataset.errorMessage || 'Something went wrong. Please try again.');
        return;
      }

      form.reset();
      this.#openModal();
    } catch {
      this.#showInlineError(this.dataset.errorMessage || 'Something went wrong. Please try again.');
    } finally {
      if (submitBtn instanceof HTMLButtonElement) {
        submitBtn.disabled = false;
        submitBtn.removeAttribute('aria-busy');
      }
    }
  };

  /**
   * @param {Record<string, string[] | string>} errors
   * @param {HTMLFormElement} form
   */
  #showFormErrors = (errors, form) => {
    const fieldMap = {
      name: 'contact[name]',
      email: 'contact[email]',
      body: 'contact[body]',
    };

    let firstMessage = '';

    Object.entries(fieldMap).forEach(([key, fieldName]) => {
      const fieldErrors = errors[key] || errors[`contact[${key}]`];
      if (!fieldErrors) return;

      const message = Array.isArray(fieldErrors) ? fieldErrors[0] : fieldErrors;
      const input = form.querySelector(`[name="${fieldName}"]`);
      const wrap = input?.closest('[data-field-wrap]');

      if (input instanceof HTMLElement) {
        input.setAttribute('aria-invalid', 'true');
      }
      if (wrap instanceof HTMLElement) {
        wrap.dataset.fieldError = 'true';
      }

      if (!firstMessage && message) firstMessage = message;
    });

    this.#showInlineError(firstMessage || this.dataset.errorMessage || 'Please check your details and try again.');
  };

  #showInlineError = (message) => {
    const errorEl = this.querySelector('[data-inline-error]');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
  };

  #clearErrors = () => {
    const errorEl = this.querySelector('[data-inline-error]');
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }

    this.querySelectorAll('[data-field-wrap]').forEach((wrap) => {
      if (wrap instanceof HTMLElement) {
        wrap.removeAttribute('data-field-error');
      }
    });

    this.querySelectorAll('[aria-invalid="true"]').forEach((input) => {
      input.removeAttribute('aria-invalid');
    });
  };

  #openModal = () => {
    const modal = this.querySelector('[data-success-modal]');
    if (!modal) return;

    modal.dataset.open = 'true';
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('rheina-contact--modal-open');

    const autoClose = Number.parseInt(this.dataset.autoCloseMs || '0', 10);
    if (autoClose > 0) {
      if (this.#autoCloseTimer !== null) clearTimeout(this.#autoCloseTimer);
      this.#autoCloseTimer = window.setTimeout(this.#closeModal, autoClose);
    }

    const closeBtn = modal.querySelector('[data-modal-close]');
    if (closeBtn instanceof HTMLElement) closeBtn.focus();
  };

  #closeModal = () => {
    const modal = this.querySelector('[data-success-modal]');
    if (!modal) return;

    modal.dataset.open = 'false';
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('rheina-contact--modal-open');

    if (this.#autoCloseTimer !== null) {
      clearTimeout(this.#autoCloseTimer);
      this.#autoCloseTimer = null;
    }
  };
}

if (!customElements.get('rheina-contact')) {
  customElements.define('rheina-contact', RheinaContact);
}
